'use client';
/**
 * 自定义 React Hook，使用 IntersectionObserver 检测元素是否在视口中。
 * 该 Hook 提供一个 ref 用于附加到目标元素，以及一个布尔值指示可见性。
 */

import { useState, useEffect, useRef } from 'react';

/**
 * 用于观察元素在视口中可见性的 Hook。
 * @template T - 要观察的 HTMLElement 类型（例如 HTMLDivElement）。
 * @returns 一个对象，包含：
 * - ref: 要附加到要观察的元素的 ref。
 * - inView: 一个布尔值，指示元素当前是否与视口相交。
 */
export function useInView<T extends HTMLElement>() {
    // 用于保存元素引用的 ref
    const ref = useRef<T | null>(null);
    // 用于跟踪元素是否在视图中的状态
    const [inView, setInView] = useState(false);

    useEffect(() => {
        // 捕获当前元素以避免清理函数中的陈旧闭包问题
        const element = ref.current;
        if (!element) {
            return () => {}; // 如果没有元素，直接返回
        }

        // 使用 20% 阈值创建 IntersectionObserver
        const observer = new IntersectionObserver(
            ([entry]) => {
                // 根据相交状态更新状态
                setInView(entry.isIntersecting);
            },
            { threshold: 0.2 }
        );

        // 开始观察元素
        observer.observe(element);

        // 清理函数以停止观察元素
        return () => {
            observer.unobserve(element);
        };
    }, []); // 空依赖数组，因为我们只想设置一次观察器

    return { ref, inView };
}
